/**
 * Unified chat — the REST + socket contract shared with the backend (`espotz-commons` chatTypes)
 * and the mobile app. Keep the wire names byte-identical to the backend's.
 */

export enum ConversationType {
  DIRECT = 'DIRECT',
  GROUP = 'GROUP',
  TEAM = 'TEAM',
  CLAN = 'CLAN',
  TOURNAMENT = 'TOURNAMENT',
  MATCH = 'MATCH',
  ORGANIZATION = 'ORGANIZATION',
}

export enum ConversationStatus {
  OPEN = 'OPEN',
  READ_ONLY = 'READ_ONLY',
  CLOSED = 'CLOSED',
}

/** Scopes whose conversation id is resolved through `GET /chat/scoped/:kind/:scopeId`. */
export enum ChatScopeKind {
  TOURNAMENT = 'TOURNAMENT',
  MATCH = 'MATCH',
  CLAN = 'CLAN',
  TEAM = 'TEAM',
  ORGANIZATION = 'ORGANIZATION',
}

/** A member's role inside one conversation. */
export enum ChatMemberRole {
  OWNER = 'OWNER',
  ADMIN = 'ADMIN',
  MEMBER = 'MEMBER',
  /** Tournament/match owner, admin or operator. */
  ORGANISER = 'ORGANISER',
}

export enum ChatMemberState {
  ACTIVE = 'ACTIVE',
  LEFT = 'LEFT',
  REMOVED = 'REMOVED',
  /** A direct message from someone the recipient isn't connected to, not accepted yet. */
  REQUEST = 'REQUEST',
}

export enum ChatPostingPolicy {
  ALL = 'ALL',
  ADMINS = 'ADMINS',
}

export enum SharedObjectKind {
  FEED_POST = 'FEED_POST',
  TOURNAMENT = 'TOURNAMENT',
  CLAN = 'CLAN',
  PROFILE = 'PROFILE',
  TEAM = 'TEAM',
  ORGANIZATION = 'ORGANIZATION',
}

export enum ChatReportTargetType {
  MESSAGE = 'MESSAGE',
  USER = 'USER',
  CONVERSATION = 'CONVERSATION',
}

/** In the order the report picker lists them. */
export enum ChatReportReason {
  SPAM = 'SPAM',
  HARASSMENT = 'HARASSMENT',
  HATE = 'HATE',
  SEXUAL = 'SEXUAL',
  VIOLENCE = 'VIOLENCE',
  SELF_HARM = 'SELF_HARM',
  SCAM = 'SCAM',
  UNDERAGE = 'UNDERAGE',
  OTHER = 'OTHER',
}

export enum InboxFilter {
  ALL = 'all',
  TOURNAMENTS = 'tournaments',
  CLANS = 'clans',
  TEAMS = 'teams',
  DIRECT = 'direct',
  GROUPS = 'groups',
  REQUESTS = 'requests',
  /** Chats with unread messages, muted ones included. */
  UNREAD = 'unread',
  ORGANIZATIONS = 'organizations',
}

export enum ChatMessageType {
  TEXT = 'TEXT',
  IMAGE = 'IMAGE',
  POST_SHARE = 'POST_SHARE',
  SYSTEM = 'SYSTEM',
}

/** Whether an announcement was posted for the whole tournament or for one match. */
export enum ChatAnnouncementScope {
  TOURNAMENT = 'TOURNAMENT',
  MATCH = 'MATCH',
}

/** What produced an announcement. Absent on the wire means `ORGANISER`. */
export enum AnnouncementKind {
  ORGANISER = 'ORGANISER',
  SCORE_ADDED = 'SCORE_ADDED',
  SCORE_UPDATED = 'SCORE_UPDATED',
}

/** What happened to an announcement, in an `announcement-changed` frame. */
export enum AnnouncementChange {
  PINNED = 'PINNED',
  UNPINNED = 'UNPINNED',
  DELETED = 'DELETED',
}

/** Where a score announcement's View scores button goes: one match's scorecard. */
export interface AnnouncementScoreLink {
  /** The stage's `level`. */
  stage: number;
  /** 0-based position of that stage in `tournament.stages`. */
  stageIndex: number;
  /** 0-based group within that stage that holds the match. */
  groupIndex: number;
  /** 1-based round key ("1", "2", ...). */
  round: string;
  matchSequence: number;
  /** 1-based game number, or `final` for a whole-match update. */
  game: number | 'final';
}

/** The two panes of a tournament or match chat. */
export enum ChatPaneTab {
  CHAT = 'chat',
  ANNOUNCEMENTS = 'announcements',
}

/** The operator label on tournament/match chat messages. */
export enum ChatSenderRole {
  OWNER = 'OWNER',
  ADMIN = 'ADMIN',
  OPERATOR = 'OPERATOR',
  PLAYER = 'PLAYER',
}

export type ChatAttachmentContentType =
  'image/jpeg' | 'image/png' | 'image/webp' | 'image/gif';

export const CHAT_ATTACHMENT_CONTENT_TYPES: readonly ChatAttachmentContentType[] =
  ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

/** Server-side limit on a message's text. */
export const CHAT_TEXT_MAX_LENGTH = 1000;
/** Server-side cap on one history page. */
export const CHAT_HISTORY_MAX_LIMIT = 50;
/** `mutedUntil` for "muted until I turn it back on". */
export const CHAT_MUTED_FOREVER = '9999-12-31T00:00:00.000Z';

export interface ChatSender {
  id: string;
  name?: string;
  username?: string;
  profilePicture?: { url?: string };
}

export interface ChatAttachmentView {
  key: string;
  kind: 'IMAGE';
  w?: number;
  h?: number;
  url?: string;
}

/** How the shared object looked when it was shared. Never shown as if it were live. */
export interface ChatSharedSnapshot {
  title: string;
  imageUrl?: string;
  authorName?: string;
  /** PROFILE shares: the route to the live profile is keyed by it. */
  username?: string;
}

export interface ChatSharedObject {
  kind: SharedObjectKind | string;
  id: string;
  snapshot: ChatSharedSnapshot;
}

/** Set on the server-posted message that shows an announcement in the chat. */
export interface ChatAnnouncementRef {
  announcementId: string;
  scope: ChatAnnouncementScope;
  matchId?: string;
  stageName?: string;
  matchNumber?: number;
  kind?: AnnouncementKind;
  /** Score announcements about one game. */
  gameNumber?: number;
  scoreLink?: AnnouncementScoreLink;
}

export interface ChatMessageView {
  id: string;
  conversationId: string;
  seq: number;
  /** `null` = a system message. */
  sender: ChatSender | null;
  senderRole?: string;
  clientMsgId?: string;
  type: ChatMessageType;
  text?: string;
  attachments?: ChatAttachmentView[];
  shared?: ChatSharedObject;
  replyToId?: string;
  /** SYSTEM messages that carry an organiser's announcement. */
  announcement?: ChatAnnouncementRef | null;
  /** A deleted message keeps its place in the sequence but has no content. */
  deleted: boolean;
  /**
   * A stub kept only so the sequence stays gapless (a direct message from someone you blocked).
   * Never rendered.
   */
  hidden?: boolean;
  createdAt: string;
}

export interface InboxItem {
  conversationId: string;
  type: ConversationType;
  status: ConversationStatus;
  title: string;
  imageUrl: string | null;
  match?: { matchSequence?: number; round?: string; stageName?: string };
  scope?: { kind: string; id: string };
  parentId?: string;
  lastMessage: {
    id: string;
    seq: number;
    from: string | null;
    type: string;
    preview: string;
    at: string;
    /** User messages: lets the row read "Name: text". Absent for system messages. */
    senderName?: string;
  } | null;
  lastMessageAt: string;
  unread: number;
  muted: boolean;
  request: boolean;
}

export interface InboxPage {
  items: InboxItem[];
  nextCursor: string | null;
}

export interface ChatMembership {
  role: ChatMemberRole | string;
  label: string | null;
  state: ChatMemberState | string;
  lastReadSeq: number;
  mutedUntil: string | null;
  muted: boolean;
}

export interface ChatConversationSettings {
  postingPolicy?: ChatPostingPolicy;
  slowModeSec?: number;
}

export interface ChatConversationDetail {
  id: string;
  type: ConversationType;
  status: ConversationStatus;
  title: string | null;
  imageUrl?: string | null;
  /** MATCH only: the client composes "Match N" in its own language. */
  match?: { matchSequence?: number; round?: string; stageName?: string };
  parentId: string | null;
  scope: { kind: string; id: string } | null;
  tournamentId?: string | null;
  memberCount: number;
  lastSeq: number;
  settings?: ChatConversationSettings;
  /** DMs: your request hasn't been accepted yet, so one message is all you can send. */
  awaitingAcceptance?: boolean;
  /** DMs awaiting acceptance: that one message has been sent, so nothing more until they accept. */
  requestMessageSent?: boolean;
  /** DMs: the other person. */
  otherUserId?: string;
  membership: ChatMembership;
}

export interface ChatConversationMember {
  userId: string;
  name?: string;
  username?: string;
  profilePicture?: { url?: string } | null;
  role: ChatMemberRole | string;
  label: string | null;
  state: ChatMemberState | string;
}

export interface ChatMembersPage {
  members: ChatConversationMember[];
  nextCursor: string | null;
}

/** Someone the people picker can offer for a new chat or group. */
export interface ChatPerson {
  id: string;
  name?: string;
  username?: string;
  profilePicture?: { url?: string } | null;
}

export interface DirectConversationResult {
  conversationId: string;
  /** True: the other person has to accept; one message may be sent until then. */
  request: boolean;
}

export interface ChatReportBody {
  targetType: ChatReportTargetType;
  targetId: string;
  reason: ChatReportReason;
  description?: string;
}

export interface ChatShareBody {
  clientMsgId: string;
  target: { kind: SharedObjectKind; id: string };
  conversationIds: string[];
  text?: string;
}

export interface ChatShareResult {
  shared: ChatSharedObject;
  results: {
    conversationId: string;
    message?: ChatMessageView;
    errorCode?: string;
  }[];
}

export interface ChatSyncEntry {
  conversationId: string;
  lastSeq: number;
  lastReadSeq: number;
  status: ConversationStatus;
}

/** Total unread across the caller's active, unmuted, non-archived chats (requests excluded). */
export interface ChatUnreadTotal {
  unread: number;
  /** How many chats that unread is spread over. */
  conversations: number;
}

export interface ChatSyncResult {
  conversations: ChatSyncEntry[];
}

export interface ChatHistoryPage {
  messages: ChatMessageView[];
  hasMore: boolean;
}

/** Exactly one of `beforeSeq` (newest first) or `afterSeq` (oldest first); neither = newest page. */
export interface ChatHistoryQuery {
  beforeSeq?: number;
  afterSeq?: number;
  limit?: number;
}

export interface SendChatMessageBody {
  clientMsgId: string;
  type: ChatMessageType.TEXT | ChatMessageType.IMAGE;
  text?: string;
  attachments?: { key: string; w?: number; h?: number }[];
  replyToId?: string;
}

export interface ChatAttachmentUpload {
  key: string;
  uploadUrl: string;
  url: string;
}

export interface ChatWsTicket {
  ticket: string;
  expiresAt: string;
}

// ─── Socket wire protocol ────────────────────────────────────────────────────

/** Client → server socket routes (the `action` field). */
export enum ChatClientOp {
  PING = 'ping',
  OPEN = 'open',
  CLOSE = 'close',
}

/** Server → client frame ops (the `op` field). */
export enum ChatServerOp {
  MESSAGE = 'message',
  MESSAGE_DELETED = 'message-deleted',
  READ = 'read',
  REMOVED = 'removed',
  CONVERSATION_UPDATED = 'conversation-updated',
  ANNOUNCEMENT_CHANGED = 'announcement-changed',
  RECONNECT = 'reconnect',
  ERROR = 'error',
}

export type ChatServerFrame =
  | {
      op: ChatServerOp.MESSAGE;
      conversationId: string;
      message: ChatMessageView;
    }
  | {
      op: ChatServerOp.MESSAGE_DELETED;
      conversationId: string;
      messageId: string;
      seq: number;
    }
  | { op: ChatServerOp.READ; conversationId: string; seq: number }
  | { op: ChatServerOp.REMOVED; conversationId: string }
  | { op: ChatServerOp.CONVERSATION_UPDATED; conversationId: string }
  | {
      /** An announcement listed in this chat's Announcements tab was pinned, unpinned or deleted. */
      op: ChatServerOp.ANNOUNCEMENT_CHANGED;
      conversationId: string;
      announcementId: string;
      change: AnnouncementChange;
    }
  | { op: ChatServerOp.RECONNECT }
  | { op: ChatServerOp.ERROR; code: string; conversationId?: string };

export enum ChatSocketStatus {
  IDLE = 'IDLE',
  CONNECTING = 'CONNECTING',
  OPEN = 'OPEN',
  RECONNECTING = 'RECONNECTING',
}

// ─── Client-side message list ────────────────────────────────────────────────

export enum ChatDeliveryState {
  SENDING = 'SENDING',
  FAILED = 'FAILED',
}

/** A message the user sent that the server hasn't confirmed yet. Keyed by `clientMsgId`. */
export interface PendingChatMessage {
  clientMsgId: string;
  type: ChatMessageType.TEXT | ChatMessageType.IMAGE;
  text?: string;
  /** Object URL of the picked image, for the bubble while it uploads. */
  previewUrl?: string;
  /** The picked image, kept so a retry can upload it again if the upload itself failed. */
  file?: File;
  /** Set once the upload succeeded, so a retry only re-sends. */
  attachment?: { key: string; w?: number; h?: number };
  state: ChatDeliveryState;
  createdAt: string;
}

export interface ChatMessageListState {
  /** Confirmed messages, ascending by `seq`, unique by `id`. */
  messages: ChatMessageView[];
  /** Optimistic sends, in the order they were made. Always rendered after `messages`. */
  pending: PendingChatMessage[];
}

/** A piece of a chat message's text: plain text, or a web link that may be opened. */
export enum ChatTextPartKind {
  TEXT = 'text',
  LINK = 'link',
}

export type ChatTextPart =
  | { kind: ChatTextPartKind.TEXT; text: string }
  | {
      kind: ChatTextPartKind.LINK;
      /** As typed in the message. */
      text: string;
      /** Absolute http(s) URL. */
      href: string;
      /** The host the link really goes to (IDNs in punycode), shown before leaving Espotz. */
      host: string;
      /** An Espotz host: opens without the warning. */
      trusted: boolean;
    };

export type ChatLinkPart = Extract<
  ChatTextPart,
  { kind: ChatTextPartKind.LINK }
>;
